// Atomic client rules (ADR-0173 fase 2, PR-1) — what the extractor emits,
// what a person reviews, and what a context version carries.
import { z } from "zod";
import { ruleResponsibleSchema } from "./context-source.js";
/** Where in the source the rule came from — enough to highlight it. */
export const ruleSourceLocationSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("pdf"),
        page: z.number().int().min(1),
        heading: z.string().max(200).optional(),
    }),
    z.object({
        kind: z.literal("xlsx"),
        sheet: z.string().min(1).max(100),
        cell: z.string().regex(/^[A-Z]{1,3}\d+$/),
    }),
    z.object({ kind: z.literal("image"), slot: z.number().int().min(0).optional() }),
]);
export const ruleParamOpSchema = z.enum([
    "eq",
    "min",
    "max",
    "range",
    "order",
    "includes",
    "presence",
]);
export const ruleParamSchema = z.object({
    name: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    op: ruleParamOpSchema,
    value: z.union([z.number(), z.string().max(200), z.array(z.string().max(100)).max(20)]),
    max: z.number().optional(),
    unit: z.string().max(30).optional(),
    scope: z.string().max(120).optional(),
});
/** `foto`: the model can judge it from a photo. `dato`: needs a datum
 *  (inventory, price list) — never sent to the model. `humano`: only a
 *  person can check (a storeroom) — never sent to the model. */
export const ruleVerificationSchema = z.enum(["foto", "dato", "humano"]);
export const ruleKindSchema = z.enum(["exhibicion", "limpieza", "precio", "grafica", "proceso"]);
export const ruleFlagSchema = z.enum([
    "ambiguo",
    "responsabilidad_en_conflicto",
    "posible_conflicto",
    "hoja_oculta",
    "texto_cortado",
    /** Set by core-api when sourceQuote is not a literal substring of the
     *  native text, or the text carries imperative language aimed at the AI. */
    "requiere_revision",
]);
/** The extractor's output for ONE rule (exploración §5). Everything the
 *  server owns (ids, version, precedence, status, validity) is absent here
 *  on purpose: the model never sets it. */
export const extractedRuleSchema = z
    .object({
    tempId: z.string().regex(/^r\d+$/),
    text: z.string().trim().min(1).max(600),
    sourceQuote: z.string().min(1).max(800),
    sourceLocation: ruleSourceLocationSchema,
    /** Client item codes (`1.1`) the rule proposes to attach to; the
     *  server validates them against the template and drops strangers. */
    linkedItemCodes: z.array(z.string().trim().min(1).max(32)).max(5),
    linkConfidence: z.enum(["alta", "media", "baja"]),
    params: z.array(ruleParamSchema).max(6),
    responsible: z.union([ruleResponsibleSchema, z.literal("no_especificado")]),
    responsibleSource: z.enum(["en_regla", "bloque_seccion", "inferido"]),
    verification: ruleVerificationSchema,
    verificationNeeds: z
        .array(z.enum(["inventario", "lista_precios", "bodega", "reservas", "otro"]))
        .optional(),
    conditional: z.object({ onStock: z.boolean(), note: z.string().max(200).optional() }),
    kind: ruleKindSchema,
    flags: z.array(ruleFlagSchema).max(8),
})
    .strict();
/** The redaction sentinel (Ley 21.719 art. 7): what a rule's text, its
 *  quote, a source's native text and the bundle copies become when a data
 *  subject's request is honoured. Same literal as findings.original_text
 *  (migration 0052); the DB triggers of 0091 (ex-0073) accept no other new value. */
export const CONTEXT_REDACTED_SENTINEL = "[REDACTADO]";
export const CONTEXT_RULE_STATUSES = [
    "draft",
    "edited",
    "accepted",
    "discarded",
    "in_conflict",
    "superseded",
];
export const contextRuleStatusSchema = z.enum(CONTEXT_RULE_STATUSES);
/** Imperatives aimed at the AI inside a rule — flagged for review, never
 *  silently accepted (exploración §5 «contención»). */
const IMPERATIVE_RE = /\b(ignora|ignorar|olvida|olvidar|siempre (devuelve|responde|cumple)|nunca (devuelvas|respondas)|responde|devuelve|contesta|cambia (de )?tarea|revela|muestra (el|tu) (prompt|mensaje|sistema))\b/i;
export function looksLikeInstructionToAi(text) {
    return IMPERATIVE_RE.test(text);
}
/** `sourceQuote` must be a literal substring of the native text, after
 *  collapsing whitespace on both sides (PDF extraction wraps lines). */
export function quoteIsLiteral(sourceQuote, nativeText) {
    const norm = (s) => s.replace(/\s+/g, " ").trim();
    const q = norm(sourceQuote);
    return q.length > 0 && norm(nativeText).includes(q);
}
//# sourceMappingURL=context-rule.js.map